import { MouseEvent, ReactNode, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

interface TooltipProviderProps {
  children: ReactNode;
  delayDuration?: number;
  skipDelayDuration?: number;
}

export function TooltipProvider({
  children,
  delayDuration = 300,
  skipDelayDuration = 150,
}: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  sideOffset = 8,
  open,
  onOpenChange,
}: TooltipProps) {
  return (
    <TooltipPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <TooltipPrimitive.Trigger asChild>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          className="z-[60] max-w-sm rounded-xl border border-slate-200 bg-white/95 px-3 py-2 text-xs text-slate-700 shadow-lg shadow-slate-200/70 transition data-[state=delayed-open]:opacity-100 data-[state=closed]:opacity-0"
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-white/95 stroke-slate-200" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

interface InfoTooltipProps {
  content: ReactNode;
  ariaLabel?: string;
}

export function InfoTooltip({ content, ariaLabel }: InfoTooltipProps) {
  const intl = useIntl();
  const label = ariaLabel ?? intl.formatMessage({ id: 'common.moreInfo' });
  const [isOpen, setIsOpen] = useState(false);
  const wasOpenOnPress = useRef<boolean | null>(null);

  function rememberOpenState() {
    wasOpenOnPress.current = isOpen;
  }

  function toggle(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    const wasOpen = wasOpenOnPress.current ?? isOpen;
    wasOpenOnPress.current = null;
    setIsOpen(!wasOpen);
  }

  return (
    <Tooltip content={content} open={isOpen} onOpenChange={setIsOpen}>
      <button
        type="button"
        aria-label={label}
        onPointerDown={rememberOpenState}
        onClick={toggle}
        className="relative inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-200/70 bg-transparent text-[10px] font-semibold text-slate-400 transition hover:border-slate-300 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-200 before:absolute before:-inset-2 before:content-['']"
      >
        ?
      </button>
    </Tooltip>
  );
}
